import * as React from "react";
import { useContent, text, link } from "../../hooks/useContent";
import { Avatar } from "../ui/Avatar";
import { Icon } from "../ui/Icon";
import { StepNumber } from "../ui/StepNumber";
import { MarkdownBody } from "./MarkdownBody";

/** Home "About Me" timeline. Lives in the parked-camera tail of the scroll
 *  scene (opaque bg over the canvas), between Reviews and the Footer. All copy
 *  is CMS-driven (about.* keys). Desktop: left rail + dashed-spine timeline.
 *  Mobile: single stack with a numbered badge per card. */
export function AboutTimeline() {
  const { data: content } = useContent();
  // Four fixed biographical milestones — CMS keys about.node1…about.node4.
  const milestones = [1, 2, 3, 4].map((n) => ({
    n: String(n),
    title: text(content, `about.node${n}.title`),
    body: text(content, `about.node${n}.body`),
  }));
  const last = milestones.length - 1;

  return (
    <section
      id="about"
      data-scroll-anchor="about"
      className="relative w-full bg-white lg:px-6 py-24 lg:py-32"
    >
      {/* t-container = the same content width every other home section uses */}
      <div className="t-container lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)] lg:gap-x-16">
        {/* Left rail: intro + portrait + connect (pinned to rail bottom on lg) */}
        <div className="lg:flex lg:flex-col lg:pr-6">
          {/* Mobile: header centred to match the Reviews header on the shared
              white surface; body copy below stays left for readability. */}
          <p className="t-eyebrow-blue text-center lg:text-left">{text(content, "about.eyebrow")}</p>
          {/* max-w forces the two-line lockup from the comp: DENNIS / VALCHEZ */}
          <h2 className="t-display mt-3 max-w-[8ch] mx-auto text-center text-primary lg:mx-0 lg:text-left">
            {text(content, "about.title")}
          </h2>
          <p className="t-subtitle-blue mt-4 text-center lg:text-left">{text(content, "about.role")}</p>
          <div className="mt-6 max-w-md">
            <MarkdownBody surface="brand" body={text(content, "about.intro")} />
          </div>

          {/* Mobile: portrait centred with the header identity block above;
              lg keeps it on the left-rail axis. */}
          <div className="mt-10 flex justify-center lg:justify-start">
            <Avatar
              size="2xl"
              src={`${process.env.PUBLIC_URL}/hero-portrait.jpg`}
              alt="Dennis Valchez"
            />
          </div>

          <div className="mt-16 max-w-md lg:mt-auto lg:pt-16">
            <h3 className="t-section text-primary">{text(content, "about.connect.title")}</h3>
            <div className="mt-4">
              <MarkdownBody surface="brand" body={text(content, "about.connect.lede")} />
            </div>
            <a
              href={link(content, "about.connect.website")}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 inline-flex items-center gap-3 text-h3 font-medium tracking-link text-primary rounded hover:text-primary-hover focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
            >
              <Icon name="globe" />
              {text(content, "about.connect.websiteLabel")}
            </a>
            <p className="mt-8 text-body font-semibold uppercase tracking-tight text-primary">
              {text(content, "about.connect.disclaimer")}
            </p>
            <div className="mt-6">
              <MarkdownBody surface="brand" body={text(content, "about.connect.closing")} />
            </div>
          </div>
        </div>

        {/* Timeline: spine + cards */}
        <ol className="mt-16 lg:mt-0 lg:grid lg:grid-cols-[9rem_minmax(0,1fr)]">
          {milestones.map((m, i) => (
            <li key={m.n} className="lg:contents">
              {/* Desktop spine cell: dashed line + node centered to the card,
                  dashed connector running from the node to the card edge */}
              <div className="relative hidden lg:flex lg:justify-center">
                <span
                  aria-hidden="true"
                  className={`absolute left-1/2 w-0 -translate-x-1/2 border-l-2 border-dashed border-primary/40 ${
                    i === 0 ? "top-1/2 bottom-0" : i === last ? "top-0 h-1/2" : "inset-y-0"
                  }`}
                />
                <span
                  aria-hidden="true"
                  className="absolute left-1/2 right-0 top-1/2 border-t-2 border-dashed border-primary/40"
                />
                <span className="absolute left-1/2 top-1/2 z-10 -translate-x-1/2 -translate-y-1/2">
                  <StepNumber n={m.n} size="node" />
                </span>
              </div>

              {/* Card */}
              <article className="mb-10 rounded-2xl bg-white p-7 shadow-[0_18px_44px_rgba(54,98,142,0.10)]">
                <h3 className="flex items-center gap-3 text-h2 font-medium uppercase tracking-link text-primary">
                  <span className="lg:hidden">
                    <StepNumber n={m.n} size="badge" />
                  </span>
                  {m.title}
                </h3>
                <div className="mt-4">
                  <MarkdownBody body={m.body} surface="light" />
                </div>
              </article>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}
